import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { SparklesIcon, CheckCircleIcon, LightBulbIcon, AcademicCapIcon, RocketLaunchIcon, GlobeAltIcon } from '@heroicons/react/24/solid';

// Ruta CORREGIDA a tu archivo UrlConfig.js
import UrlConfig from '../../../data/UrlConfig';

// Ruta CORREGIDA de la tapa del libro
import bookCover from '../../../assets/books/los-7-pasos-para-manifestar/cover.png';

const API_URLS = UrlConfig;

const SevenManifestationStepBook = () => {
  const [name, setName] = useState(''); // Estado para el nombre
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(''); // Para mensajes de éxito del backend
  const [error, setError] = useState('');     // Para mensajes de error del backend o frontend
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false); // Para controlar el estado de éxito en el frontend

  const { ref, inView } = useInView({
    threshold: 0.1, // Se activa cuando el 10% del elemento es visible
    triggerOnce: true, // Animar solo una vez
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setError('');

    // Validación básica del frontend
    if (!name.trim()) {
      setError('Por favor, ingresa tu nombre.');
      setIsLoading(false);
      return;
    }
    if (!email.trim()) {
      setError('Por favor, ingresa tu dirección de correo electrónico.');
      setIsLoading(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Por favor, ingresa una dirección de correo electrónico válida.');
      setIsLoading(false);
      return;
    }

    try {
      // Tu lógica de 'add_lead' va aquí, adaptada para el libro
      const response = await fetch(API_URLS.leads_action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          nick: name.trim(), // Usamos el nombre ingresado por el usuario
          page: 'SevenManifestationStepBook',
          section: 'book-interest-form',
          status: 'SevenManifestationBookInterest',
          newsletter: ['seven-manifestation-book'], // Etiqueta específica para el interés en el libro
          message: 'Interesado en obtener una copia del libro "7 Pasos para Manifestar una Nueva Realidad".', // Mensaje predeterminado
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('¡Gracias por tu interés! Te avisaremos cuando el libro esté disponible.');
        setName(''); // Limpiar el campo de nombre
        setEmail('');
        setSubmitted(true);
      } else {
        throw new Error(data.message || 'Ocurrió un error al procesar tu solicitud.');
      }
    } catch (err) {
      console.error("Seven Manifestation Book Subscription Error:", err);
      setError(err.message || 'Fallo al registrar tu interés. Por favor, inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col items-center py-12 px-4">
      {/* Fondo degradado */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95"></div>

      <main
        ref={ref}
        className={`relative z-10 w-full max-w-2xl mx-auto
          flex flex-col items-center text-center
          transition-all duration-700 ease-out
          ${inView ? "animate-fade-in" : "opacity-0 translate-y-8"}
        `}
      >
        {/* Sección de Información del Libro (Estilo Destacado) */}
        <div className="relative w-full mb-8 rounded-xl overflow-hidden group">
          {/* Pseudo-elemento animado para el efecto de borde/brillo destacado */}
          <div className="absolute -inset-1 rounded-xl bg-gradient-to-r from-teal-400 via-emerald-500 to-lime-500 opacity-70 blur group-hover:opacity-100 group-hover:duration-200 animate-spin-slow"></div>

          <div className="relative bg-black/50 p-6 sm:p-8 rounded-xl flex flex-col items-center justify-center transition-colors duration-300 group-hover:bg-black/70">
            {/* Imagen de la Tapa del Libro */}
            <div className="w-48 h-auto mb-6 shadow-xl rounded-md border border-gray-700">
              <img
                src={bookCover} // Usa tu imagen de la tapa del libro aquí
                alt="7 Pasos para Manifestar una Nueva Realidad - Portada del Libro"
                className="w-full h-full object-contain rounded-md"
              />
            </div>

            {/* Título del Libro */}
            <h1 className="text-3xl sm:text-4xl font-extrabold mb-2 leading-tight text-teal-300">
              7 Pasos para Manifestar una Nueva Realidad
            </h1>
            {/* Subtítulo del Libro */}
            <p className="text-lg sm:text-xl text-gray-300 mb-6">
              Aprende a co-crear tu vida con principios metafísicos y energéticos.
            </p>
          </div>
        </div>

        {/* Sección de Beneficios */}
        <div className="w-full max-w-md bg-black/30 border border-gray-700 shadow-lg shadow-gray-700/30 rounded-xl p-6 sm:p-8 mb-8 text-left">
          <h2 className="text-2xl font-semibold mb-4 text-emerald-400 text-center">
            ¿Qué aprenderás y lograrás con este libro?
          </h2>
          <ul className="space-y-3 text-gray-300">
            <li className="flex items-start">
              <LightBulbIcon className="w-6 h-6 text-yellow-400 mr-3 flex-shrink-0 mt-1" />
              <span>
                **Desbloquear tu potencial creador** y entender cómo tus pensamientos y emociones moldean tu realidad.
              </span>
            </li>
            <li className="flex items-start">
              <AcademicCapIcon className="w-6 h-6 text-blue-400 mr-3 flex-shrink-0 mt-1" />
              <span>
                Conocer y aplicar las **leyes universales de la manifestación** para atraer lo que deseas.
              </span>
            </li>
            <li className="flex items-start">
              <RocketLaunchIcon className="w-6 h-6 text-purple-400 mr-3 flex-shrink-0 mt-1" />
              <span>
                Diseñar tu realidad deseada con un **método práctico y paso a paso**, fácil de integrar en tu día a día.
              </span>
            </li>
            <li className="flex items-start">
              <SparklesIcon className="w-6 h-6 text-pink-400 mr-3 flex-shrink-0 mt-1" />
              <span>
                Superar creencias limitantes y transformar patrones negativos en **oportunidades de crecimiento**.
              </span>
            </li>
            <li className="flex items-start">
              <GlobeAltIcon className="w-6 h-6 text-cyan-400 mr-3 flex-shrink-0 mt-1" />
              <span>
                Vivir una vida más plena, consciente y alineada con tu verdadero propósito, **expandiendo tu consciencia**.
              </span>
            </li>
          </ul>
        </div>


        {/* Sección del Formulario de Obtención de Copia */}
        <div className="w-full max-w-md bg-black/30 border border-gray-700 shadow-lg shadow-gray-700/30 rounded-xl p-6 sm:p-8">
          {submitted ? (
            <div className="text-center text-green-400 p-6 bg-green-900/30 border border-green-700 rounded-lg">
              <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-xl font-semibold">{message}</p>
              <p className="text-lg">¡Estaremos en contacto contigo para que obtengas tu copia! Gracias por tu interés.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col space-y-6">
              <h2 className="text-2xl font-semibold mb-2 text-teal-400">¡Obtén tu copia ahora!</h2>
              <p className="text-gray-400 -mt-3">
                Ingresa tu nombre y correo electrónico para ser de los primeros en recibir información sobre cómo conseguir este libro transformador.
              </p>

              <div>
                <label htmlFor="name" className="block text-gray-300 text-sm font-medium mb-2 sr-only">
                  Tu Nombre
                </label> {/* sr-only oculta la etiqueta visualmente pero la mantiene para lectores de pantalla */}
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-3 rounded-md bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                  placeholder="Tu Nombre Completo"
                  required
                  disabled={isLoading}
                  aria-label="Tu nombre para la notificación del libro"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2 sr-only">
                  Tu Email
                </label> {/* sr-only oculta la etiqueta visualmente pero la mantiene para lectores de pantalla */}
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 rounded-md bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                  placeholder="tu.correo@ejemplo.com"
                  required
                  disabled={isLoading}
                  aria-label="Correo electrónico para notificaciones del libro"
                />
              </div>

              {(error || message) && (
                  <p className={`text-sm text-center ${error ? 'text-red-400' : 'text-green-400'}`}>
                      {error || message}
                  </p>
              )}

              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-lime-600 text-white font-semibold text-lg
                           hover:from-emerald-700 hover:to-lime-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500
                           transition-all duration-300 ease-in-out shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                    <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enviando...
                    </>
                ) : (
                    <>
                        Quiero mi Copia <SparklesIcon className="w-5 h-5" />
                    </>
                )}
              </button>
            </form>
          )}
        </div>
      </main>

      {/* Pie de página consistente con otras páginas */}
      <footer className="relative z-10 text-center py-6 mt-12 text-xs text-gray-600">
        © {new Date().getFullYear()} Gus • Aguiar | Todos los derechos reservados.
      </footer>
    </div>
  );
};

export default SevenManifestationStepBook;